import { camelCase, snakeCase, isEmpty } from 'lodash'

import { isKubernetes as isTargetTypeKubernetes } from 'components/attacks/create/targets/target-utils'
import { reformatSteps } from 'components/scenarios/utils/step-actions'
import { isLastIndex } from 'utils/list'
import { cleanObjKeysAndValues } from 'utils/obj'

// Based on https://github.com/gremlin/service/blob/0092548c606c0864084b56ec9b2c433c6ba30331/libraries/core/src/main/java/com/gremlininc/model/scenario/GraphNode.java#L22
export const scenarioGraphTypes = {
  InfraAttack: 'InfraAttack',
  Delay: 'Delay',
  SynchronousStatusCheck: 'SynchronousStatusCheck',
  Concurrent: 'Concurrent',
  ContinuousStatusCheck: 'ContinuousStatusCheck'
}

export const concurrentGraphTypes = [
  scenarioGraphTypes.Concurrent,
  scenarioGraphTypes.ContinuousStatusCheck
]
export const synchronousGraphTypes = [
  scenarioGraphTypes.InfraAttack,
  scenarioGraphTypes.Delay,
  scenarioGraphTypes.SynchronousStatusCheck
]

export const isScenarioGraphNode = (maybeNode) =>
  Object.values(scenarioGraphTypes).includes(maybeNode?.type)

export const isGraphNodeType = (node, type) => node?.type === type

export const isInfraAttackNode = (node) =>
  isGraphNodeType(node, scenarioGraphTypes.InfraAttack)

export const isDelayNode = (node) =>
  isGraphNodeType(node, scenarioGraphTypes.Delay)

export const isSynchronousStatusCheckNode = (node) =>
  isGraphNodeType(node, scenarioGraphTypes.SynchronousStatusCheck)

export const isConcurrentNode = (node) =>
  isGraphNodeType(node, scenarioGraphTypes.Concurrent)

export const isContinuousStatusCheckNode = (node) =>
  isGraphNodeType(node, scenarioGraphTypes.ContinuousStatusCheck)

export const createDelayNode = (changes = {}) => ({
  type: scenarioGraphTypes.Delay,
  delay: 5,
  ...changes
})

export const walkGraph = function* (graph, start = graph?.start_id) {
  if (!start) return []

  let next = start
  while (next && graph.nodes[next]) {
    const current = graph.nodes[next]
    next = current?.next
    yield current
  }
}

export const scenarioHasStatusCheck = (graph) => {
  return reformatSteps(graph).some(
    (node) =>
      isSynchronousStatusCheckNode(node) || isContinuousStatusCheckNode(node)
  )
}

export const hasAttackSteps = (scenario) =>
  getGraphNodesByType(scenario.graph, scenarioGraphTypes.InfraAttack).length > 0

export const getNodeAtIndex = (graph, index) => {
  // This is a simpler but more expensive implementation.
  // If we have have very large scenarios we could just walk the graph
  // rather than converting it to an array.
  return arrayFromGraph(graph)[index]
}

export const getNodeById = (graph, id) => graph.nodes[id]

export const arrayFromGraph = (graph) => {
  let arrayFromWalkGraph = Array.from(walkGraph(graph)).map((node) => {
    if (isConcurrentNode(node)) {
      return {
        ...node,
        branches: node.branches.map(arrayFromGraph)
      }
    }
    return node
  })

  if (isDelayNode(arrayFromWalkGraph[arrayFromWalkGraph.length - 1])) {
    arrayFromWalkGraph.splice(-1, 1)
  }
  return arrayFromWalkGraph
}

export const mapBranchedSteps = (steps, parent) =>
  steps.flatMap((step) => {
    if (step?.branches?.length) {
      return [
        step,
        ...step.branches.flatMap((branch, index) => {
          return mapBranchedSteps(branch, {
            ...step,
            branchId: `${step.id}-${index}` // use the step id as well as the index of the branch
          })
        })
      ]
    }
    if (parent) {
      return {
        ...step,
        branchId: parent.branchId,
        parent
      }
    }

    return step
  })

export const graphFromArray = (nodeArray) => {
  return nodeArray.reduce(
    (list, node, index) => {
      const isLast = isLastIndex(nodeArray, index)

      list.nodes[index] = {
        ...node,
        id: String(index)
      }

      delete list.nodes[index].next

      if (!isLast) {
        list.nodes[index].next = String(index + 1)
      }

      return list
    },
    { nodes: {}, start_id: '0' }
  )
}

export const concurrentGraphFromArray = (nodeArray) => ({
  start_id: 'concurrentNode',
  nodes: {
    concurrentNode: {
      id: 'concurrentNode',
      type: scenarioGraphTypes.Concurrent,
      branches: nodeArray
        .filter((node) => !isEmpty(node))
        .map((node) => graphFromArray(node))
    }
  }
})

export const lengthOfScenarioGraph = (graph) => Object.keys(graph.nodes).length

export const getLastScenarioGraphNode = (graph) =>
  Object.values(graph.nodes).find((node) => !node.next)

export const getGraphNodesByType = (graph, ...types) => {
  const typeSet = new Set(types)
  return mapBranchedSteps(arrayFromGraph(graph)).filter((node) =>
    typeSet.has(node.type)
  )
}

export const getNextNodeOfType = (graph, start, ...types) => {
  const sequence = walkGraph(graph, start)
  const typeSet = new Set(types)
  let done = false
  while (!done) {
    const current = sequence.next()
    done = current.done
    if (current.value && typeSet.has(current.value.type)) {
      return current.value
    }
  }
}

export function graphNodeToStepAttack(node) {
  if (
    isSynchronousStatusCheckNode(node) ||
    isContinuousStatusCheckNode(node) ||
    isConcurrentNode(node) ||
    isDelayNode(node)
  ) {
    return node
  }

  const nodeTarget = node?.target_definition ?? {}
  const nodeImpact = node?.impact_definition ?? {}
  const isKubernetes = isTargetTypeKubernetes(nodeTarget.target_type)

  // Network tags are stored in an object where the key is the tag with an array of values.
  // Casing (camelCase/snake_case) matters for these tags, so we only want to transform the path to them
  // example: trafficImpactMapping.multiSelectTags
  const trafficImpactMapping = nodeImpact.infra_command_args
    .traffic_impact_mapping
    ? cleanObjKeysAndValues(
        nodeImpact.infra_command_args.traffic_impact_mapping,
        {
          keyTransformers: [camelCase]
        }
      )
    : undefined

  const attack = {
    attackType: 'ILFI',
    attackConfiguration: node.attack_configuration,
    targetDefinition: {
      targetType: nodeTarget.target_type,
      strategyType: nodeTarget.strategy_type,
      strategy: cleanObjKeysAndValues(
        {
          ...nodeTarget.strategy,
          ...nodeTarget.strategy.attrs
        },
        {
          keysToRemove: ['attrs', 'type']
        }
      )
    },
    impactDefinition: {
      commandType: nodeImpact.infra_command_type,
      commandArgs: {
        ...cleanObjKeysAndValues(nodeImpact.infra_command_args, {
          keysToRemove: ['type', 'traffic_impact_mapping'],
          keyTransformers: [camelCase]
        }),
        trafficImpactMapping
      }
    }
  }

  if (isKubernetes) {
    attack.targetDefinition.strategy.k8sObjects = (
      nodeTarget?.k8s_objects ?? []
    ).map((target) =>
      cleanObjKeysAndValues(target, {
        keyTransformers: [camelCase]
      })
    )
    // TODO: do you want containerSelection or container_selection ?
    attack.impactDefinition.containerSelection =
      nodeTarget?.containerSelection ?? {
        selectionType: 'ANY'
      }
  }
  return attack
}

export function graphToStepsAPI(graph) {
  const nodes = arrayFromGraph(graph)
  let steps = []

  for (let i = 0; i < nodes.length; ++i) {
    const node = nodes[i]

    if (isDelayNode(node)) {
      continue
    }

    const step = {
      attacks: [graphNodeToStepAttack(node)]
    }

    const nextNode = nodes[i + 1]
    if (nextNode && isDelayNode(nextNode)) {
      step.delay = nextNode.delay
    }

    steps.push(step)
  }

  return steps
}

export function stepAttackToGraphNode(step) {
  const attack = step.attacks[0]
  const attackTarget = attack.targetDefinition
  const attackImpact = attack.impactDefinition
  const isKubernetes = isTargetTypeKubernetes(attackTarget.targetType)

  const targetStrategy = {
    attrs: cleanObjKeysAndValues(attackTarget.strategy, {
      keysToRemove: [
        'percentage',
        'count',
        'allHosts',
        'allContainers',
        'k8sObjects'
      ]
    })
  }

  if (attackTarget.strategy.allHosts) {
    targetStrategy.all_hosts = true
  } else if (attackTarget.strategy.allContainers) {
    targetStrategy.all_containers = true
  }

  if (attackTarget.strategyType === 'Random') {
    if (attackTarget.strategy.percentage) {
      targetStrategy.type = 'RandomPercent'
      targetStrategy.percentage = attackTarget.strategy.percentage
    } else {
      targetStrategy.type = 'RandomCount'
      targetStrategy.count = attackTarget.strategy.count
    }
  } else {
    targetStrategy.type = 'Exact'
  }

  // Network tags are stored in an object where the key is the tag with an array of values.
  // Casing (camelCase/snake_case) matters for these tags, so we only want to transform the path to them
  // example: trafficImpactMapping.multiSelectTags
  const traffic_impact_mapping = attackImpact.commandArgs.trafficImpactMapping
    ? cleanObjKeysAndValues(attackImpact.commandArgs.trafficImpactMapping, {
        keyTransformers: [snakeCase]
      })
    : undefined

  const node = {
    target_definition: {
      strategy_type: attackTarget.strategyType,
      target_type: attackTarget.targetType,
      strategy: targetStrategy
    },
    impact_definition: {
      infra_command_type: attackImpact.commandType,
      infra_command_args: {
        ...cleanObjKeysAndValues(attackImpact.commandArgs, {
          keysToRemove: ['commandType', 'trafficImpactMapping'],
          keyTransformers: [snakeCase]
        }),
        traffic_impact_mapping,
        type: attackImpact.commandType
      }
    },
    type: scenarioGraphTypes.InfraAttack,
    id: step.id
  }

  if (isKubernetes) {
    node.target_definition.k8s_objects = (
      attack?.targetDefinition?.strategy?.k8sObjects ?? []
    ).map((target) =>
      cleanObjKeysAndValues(target, {
        keyTransformers: [snakeCase]
      })
    )
  }
  return node
}

export function stepsToGraphAPI(steps) {
  const graph = {}
  const nodeArray = steps.reduce((stepNodes, step, index) => {
    const isLastStep = isLastIndex(steps, index)

    const node = stepAttackToGraphNode(step)

    stepNodes.push(node)

    if (!isLastStep && step.delay) {
      stepNodes.push({
        delay: step.delay,
        id: (index + 1).toString(),
        type: scenarioGraphTypes.Delay
      })
    }

    return stepNodes
  }, [])

  const nodes = nodeArray.map((node, index) => {
    const n = {
      ...node,
      id: `${index}`
    }
    if (!isLastIndex(nodeArray, index)) {
      n.next = `${index + 1}`
    }

    return [index, n]
  })

  graph.nodes = Object.fromEntries(nodes)
  graph.start_id = '0'

  return graph
}

// allows us to recursively strip parent from steps
export const stripParentFromSteps = (steps) => {
  let newSteps = [...steps]

  newSteps.map((step) => {
    delete step.parent
    if (step.branches) {
      step.branches = stripParentFromSteps(step.branches)
    }
    return step
  })

  return newSteps
}

// we do this both when adding/editing Steps and when saving scenario
// TODO: Concurrent Attacks Project: Convert to mapping Concurrent nodes instead of dividing between continuous status check and synchronous graphs
export const formatRequestBodyGraph = (steps) => {
  let nodeGraph = graphFromArray(stripParentFromSteps(steps))
  let graph

  let continuousStatusChecksGraph = getGraphNodesByType(
    nodeGraph,
    scenarioGraphTypes.ContinuousStatusCheck
  ).map((node) => [node])

  let synchronousGraph = getGraphNodesByType(
    nodeGraph,
    ...synchronousGraphTypes
  )

  if (continuousStatusChecksGraph.length > 0) {
    graph = concurrentGraphFromArray([
      ...continuousStatusChecksGraph,
      synchronousGraph
    ])
  } else {
    // If there are no continuous status checks,
    // there are no concurrent nodes. Remove parent parameter.
    synchronousGraph.map((step) => {
      delete step.branchId
    })
    graph = graphFromArray(synchronousGraph)
  }
  return graph
}

export const getIndentLevel = (step, previousStep) => {
  const currentBranchId = step?.branchId
  const previousBranchId = previousStep?.branchId

  const hasSameBranchId =
    currentBranchId && previousBranchId && currentBranchId === previousBranchId

  const shouldIndent =
    // indent if previous step is a continuous status check node or they share the same branch
    (isContinuousStatusCheckNode(previousStep) || hasSameBranchId) &&
    // and if the current step isn't a status check node
    !isContinuousStatusCheckNode(step)

  return shouldIndent ? 1 : 0
}

export const getInteractiveNodes = (steps) =>
  steps.filter((step) => !isDelayNode(step) && !isConcurrentNode(step))

export const findRunStep = (run, currentStep, preferredDefault) =>
  run && currentStep
    ? reformatSteps(run.graph).find(
        (currRun) => getStepId(currRun) === currentStep
      )
    : preferredDefault ?? null

export const getStepId = (step) =>
  step?.guid ?? `${step?.branchId ?? ''}${step?.id}`
